import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

// Modal
import ScopeOfWorkSelectModal from '../../Modals/ScopeOfWorkSelectModal';

// Utils
import messages from '../../../static/messages';

// Services
import proposalsServices from '../../../services/proposalsServices';
import scopeOfWorkServices from '../../../services/scopeOfWorkServices';
import equipmentModelsServices from '../../../services/equipmentModelsServices';

// Drag and Drop
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

// Auto-save
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';
import SimpleEditor from '../../SimpleEditor';
import { prepareHtml } from '../../../utils/htmlUtils';

// DnD Components
const DragHandle = SortableHandle(() => (
  <DragIndicatorIcon sx={{ cursor: 'grab', color: '#999', '&:hover': { color: '#333' } }} />
));

const SortableTableRow = SortableElement(({ children, ...props }) => (
  <TableRow {...props}>{children}</TableRow>
));

const SortableTableBody = SortableContainer(({ children }) => (
  <TableBody>{children}</TableBody>
));

const ProposalScopeOfWorkSection = ({ proposal, id, refresh }) => {
  const [scopeOfWorks, setScopeOfWorks] = useState([]);
  const [scopeOfWorkList, setScopeOfWorkList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [addMode, setAddMode] = useState('catalog'); // 'catalog' or 'custom'
  const [selectedItem, setSelectedItem] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [multiSelectOpen, setMultiSelectOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const dirtyRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  // Auto-save function
  const saveScopeOfWorks = useCallback(async (data) => {
    const response = await proposalsServices.update(id, { scopeOfWorks: data });
    dirtyRef.current = false;
    if (response.status === 200 && refresh) {
      refresh();
    }
  }, [id, refresh]);

  // Save immediately on user actions (add/edit/delete/reorder) — no timer
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveScopeOfWorks);

  const saveNowSafe = useCallback((data) => {
    dirtyRef.current = true;
    saveNow(data).catch(() => {
      toast.error('Auto-save failed. Please retry the action.');
    });
  }, [saveNow]);

  // Equipment model names for validation (prevent typing equipment specs into Scope of Work)
  const [equipmentModelNames, setEquipmentModelNames] = useState([]);

  // Load available scope of work items
  const loadScopeOfWorkList = async () => {
    try {
      const response = await scopeOfWorkServices.getAllWorks(0, 1000000, false);
      if (response.status === 206 || response.status === 200) {
        setScopeOfWorkList(response.data.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Load equipment model names for custom text validation
  const loadEquipmentModels = async () => {
    try {
      const response = await equipmentModelsServices.getAll({ skip: 0, take: 1000000 });
      if (response.data?.data) {
        const names = response.data.data
          .map((m) => m.name?.trim())
          .filter((n) => n && n.length >= 4); // Only meaningful model numbers (4+ chars)
        setEquipmentModelNames(names);
      }
    } catch (error) {
      console.log('Failed to load equipment models for validation:', error);
    }
  };

  /**
   * Validate custom Scope of Work text.
   * Returns true if the text is invalid (contains equipment model data).
   */
  const isEquipmentDescription = (text) => {
    if (!text) return false;
    const upper = text.toUpperCase();

    // Check for "MFG" keyword (manufacturer model reference)
    if (/\bMFG\s*[:]/i.test(text)) return true;

    // Check for equipment model numbers from catalog
    for (const modelName of equipmentModelNames) {
      if (upper.includes(modelName.toUpperCase())) return true;
    }

    return false;
  };

  // Handler functions
  const handleAdd = () => {
    let newItem;

    if (addMode === 'catalog') {
      if (!selectedItem) {
        toast.error('Please select a scope of work item');
        return;
      }

      const item = scopeOfWorkList.find((w) => w.id === selectedItem);
      if (!item) return;

      // Check if already added
      if (scopeOfWorks.some((w) => w.id === item.id)) {
        toast.warning('This item is already added');
        return;
      }

      newItem = {
        id: item.id,
        description: item.description,
        price: Number(item.price) || 0,
        isCustom: false,
      };
    } else {
      // Custom mode
      if (!customDescription.trim()) {
        toast.error('Please enter a description');
        return;
      }

      // Validate: prevent entering equipment specifications into Scope of Work
      if (isEquipmentDescription(customDescription)) {
        toast.error(
          '⚠️ Equipment model numbers must not be entered in Scope of Work. Use the Equipment section to add equipment. Scope of Work has been cleared.',
          { autoClose: 8000 }
        );
        // Clear ALL scope of work items
        setScopeOfWorks([]);
        saveNowSafe([]);
        resetAddForm();
        return;
      }

      newItem = {
        id: `custom-${Date.now()}`,
        description: customDescription.trim(),
        price: Number(customPrice) || 0,
        isCustom: true,
      };
    }

    const updated = [...scopeOfWorks, newItem];
    setScopeOfWorks(updated);
    saveNowSafe(updated);
    resetAddForm();
  };

  const resetAddForm = () => {
    setAdding(false);
    setAddMode('catalog');
    setSelectedItem('');
    setCustomDescription('');
    setCustomPrice('');
  };

  // Handle adding multiple items from modal
  const handleAddMultiple = (items) => {
    const newItems = items.map(item => ({
      id: item.id,
      description: item.description,
      price: Number(item.price) || 0,
      isCustom: false,
    }));
    
    const updated = [...scopeOfWorks, ...newItems];
    setScopeOfWorks(updated);
    saveNowSafe(updated);
    toast.success(`Added ${items.length} items`);
  };

  const handleDelete = (itemId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('this scope of work item'),
    })
      .then(() => {
        const updated = scopeOfWorks.filter((w) => w.id !== itemId);
        setScopeOfWorks(updated);
        saveNowSafe(updated);
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleEditStart = (item) => {
    setEditingItemId(item.id);
    setEditDescription(item.description);
    setEditPrice(String(item.price || 0));
  };

  const handleEditSave = () => {
    const plainText = editDescription.replace(/<[^>]*>/g, '').trim();
    if (!plainText) {
      toast.error('Description cannot be empty');
      return;
    }
    const updated = scopeOfWorks.map((w) =>
      w.id === editingItemId
        ? { ...w, description: editDescription, price: Number(editPrice) || 0 }
        : w
    );
    setScopeOfWorks(updated);
    saveNowSafe(updated);
    setEditingItemId(null);
  };

  const handleEditCancel = () => {
    setEditingItemId(null);
  };

  // Handle drag and drop reorder
  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const updated = arrayMoveImmutable(scopeOfWorks, oldIndex, newIndex);
    setScopeOfWorks(updated);
    saveNowSafe(updated);
  };

  // Calculate total
  const totalPrice = scopeOfWorks.reduce((sum, w) => sum + (Number(w.price) || 0), 0);

  // Hooks
  useEffect(() => {
    loadScopeOfWorkList();
    loadEquipmentModels();
  }, []);

  useEffect(() => {
    if (proposal?.scopeOfWorks && !dirtyRef.current) {
      setScopeOfWorks(proposal.scopeOfWorks);
    }
  }, [proposal]);

  // Validate existing custom items when equipment models are loaded
  useEffect(() => {
    if (equipmentModelNames.length === 0 || scopeOfWorks.length === 0) return;

    const hasInvalidCustom = scopeOfWorks.some(
      (item) => item.isCustom && isEquipmentDescription(item.description)
    );

    if (hasInvalidCustom) {
      toast.error(
        '⚠️ Equipment model numbers detected in Scope of Work. Scope of Work has been cleared. Use the Equipment section to add equipment.',
        { autoClose: 8000 }
      );
      setScopeOfWorks([]);
      saveNowSafe([]);
    }
  }, [equipmentModelNames]); // Only run when models finish loading

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        mb: 2 
      }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
          Scope of Work
          <Typography component="span" sx={{ ml: 2, color: 'primary.main', fontWeight: 600, fontSize: isMobile ? '0.875rem' : 'inherit' }}>
            Total: ${totalPrice.toFixed(2)}
          </Typography>
        </Typography>
        <AutoSaveIndicator
            isSaving={isSaving}
            hasUnsavedChanges={hasUnsavedChanges}
            lastSaved={lastSaved}
            error={error}
            getLastSavedText={getLastSavedText}
            variant="chip"
          />
      </Box>
      {scopeOfWorks.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No scope of work items added. Click "Add Scope of Work" to add items from the catalog or enter custom.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: isMobile ? 'unset' : 400 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}></TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" sx={{ width: isMobile ? 70 : 120 }}>Price</TableCell>
                  <TableCell align="center" sx={{ width: isMobile ? 40 : 60 }}></TableCell>
                </TableRow>
              </TableHead>
              <SortableTableBody useDragHandle onSortEnd={handleSortEnd} helperClass="sortable-row-helper">
                {scopeOfWorks.map((item, index) => (
                  <SortableTableRow key={`sow-${item.id}`} index={index}>
                    <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}>
                      <DragHandle />
                    </TableCell>
                    {editingItemId === item.id ? (
                      <>
                        <TableCell>
                          <SimpleEditor
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            minHeight={80}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: isMobile ? 70 : 120 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            sx={{ width: isMobile ? 70 : 100 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); if (e.key === 'Escape') handleEditCancel(); }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ p: isMobile ? '2px' : undefined, whiteSpace: 'nowrap' }}>
                          <Tooltip title="Save">
                            <IconButton size="small" onClick={handleEditSave} color="primary">
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton size="small" onClick={handleEditCancel}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <Box
                            component="span"
                            dangerouslySetInnerHTML={{ __html: prepareHtml(item.description) }}
                            sx={{ '& p': { margin: 0 }, '& div': { margin: 0 } }}
                          />
                          {item.isCustom && (
                            <Typography component="span" sx={{ ml: 1, color: 'warning.main', fontSize: '0.75rem' }}>
                              (Custom)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>
                          ${parseFloat(item.price || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="center" sx={{ p: isMobile ? '2px' : undefined, whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditStart(item)} color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </>
                    )}
                  </SortableTableRow>
                ))}
              </SortableTableBody>
            </Table>
          </TableContainer>

          {/* Total Row */}
          <Box sx={{ mt: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, textAlign: 'right' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Scope of Work Total: ${totalPrice.toFixed(2)}
            </Typography>
          </Box>
        </>
      )}

      {/* Add new item */}
      {adding && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          {/* Mode toggle */}
          <ToggleButtonGroup
            value={addMode}
            exclusive
            onChange={(e, val) => val && setAddMode(val)}
            size="small"
            sx={{ mb: 2 }}
            fullWidth={isMobile}
          >
            <ToggleButton value="catalog" sx={{ flex: isMobile ? 1 : 'unset' }}>From Catalog</ToggleButton>
            <ToggleButton value="custom" sx={{ flex: isMobile ? 1 : 'unset' }}>Custom</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: isMobile ? 'stretch' : 'center', 
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap' 
          }}>
            {addMode === 'catalog' ? (
              <FormControl size="small" sx={{ minWidth: isMobile ? 'unset' : 400, width: isMobile ? '100%' : 'auto' }}>
                <InputLabel>Select Scope of Work</InputLabel>
                <Select
                  value={selectedItem}
                  onChange={(e) => setSelectedItem(e.target.value)}
                  label="Select Scope of Work"
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 300 }
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>Select from list</MenuItem>
                  {scopeOfWorkList.map((w) => (
                    <MenuItem
                      key={w.id}
                      value={w.id}
                      disabled={scopeOfWorks.some((sw) => sw.id === w.id)}
                      sx={{ 
                        whiteSpace: 'normal', 
                        fontSize: isMobile ? '0.8rem' : 'inherit',
                        py: isMobile ? 1 : 0.75
                      }}
                    >
                      {w.description} - ${w.price}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <TextField
                  size="small"
                  label="Description"
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  sx={{ minWidth: isMobile ? 'unset' : 300, width: isMobile ? '100%' : 'auto' }}
                  placeholder="Enter custom scope of work"
                  fullWidth={isMobile}
                />
                <TextField
                  size="small"
                  label="Price"
                  type="number"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  sx={{ width: isMobile ? '100%' : 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </>
            )}
            <Grid container spacing={1} sx={{ width: isMobile ? '100%' : 'auto' }}>
              <Grid item xs={6} sm="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton size="small" color="primary" onClick={handleAdd}>
                  <SaveIcon />
                </IconButton>
              </Grid>
              <Grid item xs={6} sm="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton size="small" onClick={resetAddForm}>
                  <CancelIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}


      {/* Add button at bottom */}
      {/* Action Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAdding(true)}
        >
          Add Single
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<PlaylistAddIcon />}
          onClick={() => setMultiSelectOpen(true)}
        >
          Add Multiple
        </Button>
      </Box>

      {/* Multi-select Modal */}
      <ScopeOfWorkSelectModal
        open={multiSelectOpen}
        onClose={() => setMultiSelectOpen(false)}
        scopeOfWorkList={scopeOfWorkList}
        existingItems={scopeOfWorks}
        onAdd={handleAddMultiple}
      />
    </Box>
  );
};

export default ProposalScopeOfWorkSection;
